import React, { lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { HERO } from '@/constants/testIds';

const HeroCanvas = lazy(() => import('@/components/HeroCanvas'));

const PHRASES = [
  'Your Next Client.',
  'Your Restaurant’s First Impression.',
  'Your Café’s Digital Storefront.',
  'Your Brand’s Online Home.',
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function Hero() {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % PHRASES.length), 3200);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-[100vh] flex items-center overflow-hidden pt-24 pb-16"
      data-testid="hero-block"
    >
      {/* Soft animated honeycomb backdrop — SVG */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1440 900">
          <defs>
            <pattern id="heroHexBg" x="0" y="0" width="60" height="52" patternUnits="userSpaceOnUse">
              <polygon points="30,2 56,16 56,42 30,56 4,42 4,16" fill="none" stroke="rgba(15, 26, 51, 0.06)" strokeWidth="0.8" />
            </pattern>
            <radialGradient id="heroMask" cx="50%" cy="50%" r="60%">
              <stop offset="0%" stopColor="#000" stopOpacity="1" />
              <stop offset="100%" stopColor="#000" stopOpacity="0" />
            </radialGradient>
            <mask id="heroPatternMask">
              <rect width="100%" height="100%" fill="url(#heroMask)" />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="url(#heroHexBg)" mask="url(#heroPatternMask)" />
        </svg>
      </div>

      {/* 3D honeycomb cluster (right side, decorative) */}
      <div
        className="absolute inset-y-0 right-[-6%] w-1/2 lg:w-[48%] pointer-events-none hidden md:block z-0"
        style={{ maskImage: 'radial-gradient(circle at 65% 50%, #000 45%, transparent 75%)', WebkitMaskImage: 'radial-gradient(circle at 65% 50%, #000 45%, transparent 75%)', opacity: 0.85 }}
      >
        <Suspense fallback={null}>
          <HeroCanvas />
        </Suspense>
      </div>

      {/* Decorative animated SVG hex constellation top-right — visible on mobile too */}
      <div className="absolute top-24 -right-6 md:hidden pointer-events-none" aria-hidden>
        <svg width="180" height="180" viewBox="0 0 180 180">
          <defs>
            <linearGradient id="heroMobHex" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#F5C56D" />
              <stop offset="100%" stopColor="#E89A1F" />
            </linearGradient>
          </defs>
          {[[90, 50, 18], [120, 80, 14], [60, 80, 14], [90, 110, 10]].map(([cx, cy, r], i) => (
            <motion.polygon
              key={i}
              points={`${cx},${cy - r} ${cx + r * 0.866},${cy - r / 2} ${cx + r * 0.866},${cy + r / 2} ${cx},${cy + r} ${cx - r * 0.866},${cy + r / 2} ${cx - r * 0.866},${cy - r / 2}`}
              fill={i === 0 ? 'url(#heroMobHex)' : 'rgba(15, 26, 51, 0.08)'}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + i * 0.12, type: 'spring', stiffness: 200, damping: 16 }}
              style={{ transformOrigin: `${cx}px ${cy}px` }}
            />
          ))}
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 container-pro px-6 md:px-8">
        <div className="max-w-2xl lg:max-w-3xl">
          <motion.span
            className="eyebrow mb-6"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            A WEB STUDIO BUILT FOR INDIA
          </motion.span>

          <motion.h1
            className="font-display font-semibold text-[2.4rem] sm:text-5xl md:text-6xl lg:text-[4.4rem] leading-[1.04] tracking-tight text-[color:var(--sh-ink)] relative z-10"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            data-testid={HERO.headlinePrimary}
          >
            <span className="block">We don’t just build websites.</span>
            <span className="block mt-2" data-testid={HERO.headlineCycle}>
              We build{' '}
              <span className="relative inline-block align-baseline">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={idx}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
                    className="text-[color:var(--sh-amber-2)] italic font-display inline-block"
                    style={{ fontStyle: 'italic' }}
                  >
                    {PHRASES[idx]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </span>
          </motion.h1>

          <motion.p
            className="mt-7 text-[17px] md:text-lg text-[color:var(--sh-muted)] max-w-2xl leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            A small studio building fast, considered websites for restaurants, cafés, and growing
            businesses across India. We ship in days, not months.
          </motion.p>

          <motion.div
            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <button
              data-testid={HERO.ctaQuote}
              onClick={() => scrollToId('contact')}
              className="btn-amber inline-flex items-center gap-2"
            >
              Get a Free Quote
              <ArrowRight size={15} />
            </button>
            <button
              data-testid={HERO.ctaSeeWork}
              onClick={() => scrollToId('work')}
              className="btn-ghost inline-flex items-center gap-2"
            >
              See What We’ve Built
            </button>
          </motion.div>

          <div className="mt-7 md:hidden inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-600/30 bg-emerald-50 text-emerald-800 text-[11px]">
            <span className="live-dot" />
            <span className="font-accent tracking-wide">3 builds in progress</span>
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <motion.button
        data-testid={HERO.scrollHint}
        onClick={() => scrollToId('stats')}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 text-[color:var(--sh-muted)]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
      >
        <span className="font-accent text-[10px] tracking-[0.3em]">SCROLL</span>
        <motion.span animate={{ y: [0, 5, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
}
